import { Head, Link, router } from '@inertiajs/react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type Participant = {
  type: 'Team' | 'School'
  id: string
  name: string | null
}

type PracticeMatchDetail = {
  id: string
  scheduledAt: string
  location: string | null
  notes: string | null
  home: Participant
  away: Participant
  createdAt: string
  updatedAt: string
}

type Props = {
  practiceMatch: PracticeMatchDetail
  breadcrumbs: Breadcrumb[]
}

export default function AdminPracticeMatchShow({ practiceMatch, breadcrumbs }: Props) {
  const handleDestroy = () => {
    if (!window.confirm('この練習試合を削除します。よろしいですか？')) return
    router.delete(`/admin/practice_matches/${practiceMatch.id}`)
  }

  const kindLabel = practiceMatch.home.type === 'Team' ? 'クラブチーム同士' : '高校同士'

  return (
    <AppShell>
      <Head title={`練習試合 ${practiceMatch.home.name} vs ${practiceMatch.away.name}`} />
      <PageHeader
        title={`${practiceMatch.home.name ?? '?'} vs ${practiceMatch.away.name ?? '?'}`}
        description={`${kindLabel}の練習試合詳細`}
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="secondary" size="sm">
              <Link href={`/admin/practice_matches/${practiceMatch.id}/edit`}>編集</Link>
            </Button>
            <Button type="button" variant="danger" size="sm" onClick={handleDestroy}>
              削除
            </Button>
          </div>
        }
      />

      <Card padding="lg" className="space-y-6 max-w-3xl">
        <Section title="基本情報">
          <InfoGrid
            items={[
              { label: '日時', value: formatDateTime(practiceMatch.scheduledAt) },
              { label: '対戦種別', value: kindLabel },
              { label: 'ホーム', value: practiceMatch.home.name ?? '未設定' },
              { label: 'アウェー', value: practiceMatch.away.name ?? '未設定' },
              { label: '会場', value: practiceMatch.location ?? '未設定' },
            ]}
          />
        </Section>
        {practiceMatch.notes ? (
          <Section title="備考">
            <p className="whitespace-pre-line text-sm text-white/80">{practiceMatch.notes}</p>
          </Section>
        ) : null}
      </Card>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      {children}
    </section>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <dt className="text-xs uppercase tracking-wide text-white/40">{item.label}</dt>
          <dd className="mt-2 text-sm text-white/80">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
